import { useEffect, useState } from 'react';
import z from 'zod';
import { RegionUtil } from '../utils/region-util';
import { useSession } from './use-session';

function parseStateId(stateId: string | null) {
    if (stateId === null) return null;

    const parsedStateId = z.coerce.number().safeParse(stateId);

    if (!parsedStateId.success) return null;

    return parsedStateId.data;
}

export function useAutoLoginRedirect({
    stateId,
    redirect,
    onAlreadyLoggedIn,
}: {
    stateId: string | null;
    redirect: string | null;
    onAlreadyLoggedIn: (redirectUrl: string) => void;
}) {
    const [
        isRedirectingToLogin,
        setIsRedirectingToLogin,
    ] = useState(() => parseStateId(stateId) !== null);
    const session = useSession();

    // Auto redirect to the applicable state
    // if we have a stateId in given to the hook
    useEffect(() => {
        if (session.isLoading) return;
        if (
            !session.isError
            && session.data !== undefined
            && !session.data.requiresRelogin
            && !session.data.isImpersonating
        ) return;

        const parsedStateId = parseStateId(stateId);

        if (parsedStateId === null) return;

        setIsRedirectingToLogin(true);

        const loginUrl = RegionUtil.getRedirectLoginUrl(
            parsedStateId,
            encodeURI(redirect ?? '/'),
            ''
        );

        window.location.assign(loginUrl);
    }, [stateId, redirect, session.isError, session.isLoading, session.data]);

    // If the user is already logged in, and doesn't require
    // login again, redirect to the given redirect URL
    useEffect(() => {
        if (session.isLoading || session.isError) return;
        if (session.data === undefined) return;
        if (session.data.requiresRelogin) return;
        if (session.data.isImpersonating) return;

        if (redirect === null) {
            onAlreadyLoggedIn('/');
            return;
        }

        onAlreadyLoggedIn(decodeURIComponent(redirect));
    }, [
        session.data,
        session.isError,
        session.isLoading,
        redirect,
        onAlreadyLoggedIn,
    ]);

    return {
        isLoading:
            session.isLoading
            || (
                !session.isError
                && session.data !== undefined
                && !session.data.requiresRelogin
            ),
        isRedirectingToLogin,
    };
}
